import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'
import {
  SOCIAL_BRIEF_MAX_LENGTH,
  SOCIAL_MAX_TOPICS,
  SOCIAL_NOTE_MAX_LENGTH,
  SOCIAL_TOPIC_NAME_MAX_LENGTH,
} from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Text } from '@/components/ui/Text'
import { Textarea } from '@/components/ui/textarea'

import type {
  SocialRelevanceFieldsProps,
  SocialThemeFieldsProps,
} from '@/types/components/social-tracker-dialog'

/** Relevance rules sit beside the tracker terms they disambiguate. */
export function SocialRelevanceFields({
  brief,
  onBriefChange,
}: SocialRelevanceFieldsProps) {
  const { t } = useTranslation('app')
  return (
    <details className="group" data-testid="social-tracker-relevance">
      <summary className="cursor-pointer text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {t('socialListening.form.relevance')}
      </summary>
      <div className="flex flex-col gap-3 pt-4">
        <Text as="span" size="xs" color="muted" className="block">
          {t('socialListening.form.relevanceHint')}
        </Text>

        <div className="space-y-1.5">
          <Label htmlFor="social-tracker-brief-include">
            {t('socialListening.form.briefInclude')}
          </Label>
          <Textarea
            id="social-tracker-brief-include"
            rows={2}
            maxLength={SOCIAL_BRIEF_MAX_LENGTH}
            value={brief.include}
            placeholder={t('socialListening.form.briefIncludePlaceholder')}
            data-testid="social-tracker-brief-include"
            onChange={(event) =>
              onBriefChange({ ...brief, include: event.target.value })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="social-tracker-brief-exclude">
            {t('socialListening.form.briefExclude')}
          </Label>
          <Textarea
            id="social-tracker-brief-exclude"
            rows={2}
            maxLength={SOCIAL_BRIEF_MAX_LENGTH}
            value={brief.exclude}
            placeholder={t('socialListening.form.briefExcludePlaceholder')}
            data-testid="social-tracker-brief-exclude"
            onChange={(event) =>
              onBriefChange({ ...brief, exclude: event.target.value })
            }
          />
        </div>
      </div>
    </details>
  )
}

export function SocialThemeFields({
  topics,
  onTopicsChange,
}: SocialThemeFieldsProps) {
  const { t } = useTranslation('app')
  function updateTopic(
    index: number,
    patch: Partial<SocialThemeFieldsProps['topics'][number]>,
  ) {
    onTopicsChange(
      topics.map((topic, i) => (i === index ? { ...topic, ...patch } : topic)),
    )
  }

  return (
    <details className="py-4">
      <summary className="cursor-pointer text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {t('socialListening.form.topics')}
      </summary>
      <div className="space-y-3 pt-4" data-testid="social-tracker-topics">
        <Text as="span" size="xs" color="muted" className="block">
          {t('socialListening.form.topicsHint')}
        </Text>
        {topics.map((topic, index) => (
          <div
            key={topic.id ?? `new-${index}`}
            className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-2"
            data-testid="social-tracker-topic"
          >
            <Input
              aria-label={t('socialListening.form.topicName')}
              maxLength={SOCIAL_TOPIC_NAME_MAX_LENGTH}
              value={topic.name}
              placeholder={t('socialListening.form.topicName')}
              data-testid={`social-tracker-topic-name-${index}`}
              onChange={(event) =>
                updateTopic(index, { name: event.target.value })
              }
            />
            <Input
              aria-label={t('socialListening.form.topicDescription')}
              maxLength={SOCIAL_NOTE_MAX_LENGTH}
              value={topic.description}
              placeholder={t('socialListening.form.topicDescription')}
              data-testid={`social-tracker-topic-description-${index}`}
              onChange={(event) =>
                updateTopic(index, { description: event.target.value })
              }
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('socialListening.form.removeTopic', {
                name: topic.name,
              })}
              data-testid={`social-tracker-topic-remove-${index}`}
              onClick={() =>
                onTopicsChange(topics.filter((_, i) => i !== index))
              }
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {topics.length < SOCIAL_MAX_TOPICS ? (
          <Button
            type="button"
            variant="outline"
            shape="pill"
            size="sm"
            data-testid="social-tracker-topic-add"
            onClick={() =>
              onTopicsChange([...topics, { name: '', description: '' }])
            }
          >
            <Plus className="h-4 w-4" />
            {t('socialListening.form.addTopic')}
          </Button>
        ) : null}
      </div>
    </details>
  )
}
